import { EntitySelectors, EntityState } from '@ngrx/entity/src/models';
import { createSelector, DefaultProjectorFn, MemoizedSelector } from '@ngrx/store';

import {
  AuthSelectors,
  AuthState,
  CircuitDetails,
  ConfigurationSelectors,
  DeviceTypes,
  DIAGNOSIS_ANOMALY,
  Gateway,
  GatewayDevice,
  GatewayState,
  Helpers,
  InstallationDetailsSelectors,
  InstallationDetailsState,
  InstallationFeaturesSelectors,
  isPhotovoltaic,
  isReady,
  isSolarLogGateway,
  isSolarLogInverter,
  isWallboxDevice,
  NormalOperatingProgramType,
  sumArrays,
  TrendingData,
  TrendingSelectors,
  WallboxDevicesWithMatchingBoilerSerial,
} from '@store';

import {
  ActivateDeactivateCommands,
  AggregatedStatus,
  BatteryInformationFeatures,
  BatteryInformationFeaturesType,
  BatteryInternalComponentsProperties,
  CallForHeatDemandProperties,
  CircuitProperty,
  DcSensorValueProperties,
  DcTypes,
  DeviceCapability,
  DeviceIds,
  DeviceRoles,
  DiagnosisData,
  energyStorageProperties,
  energyStorageTotalPropertiesForIndividualVitocharge,
  energyStorageTotalPropertiesForMultipleVitocharge,
  Feature,
  FeatureNames,
  GatewayDevicesProperties,
  HeatingBoilerPumpInternalProperties,
  HeatingCircuitCommands,
  HeatingConfigurationRegulationProperties,
  HeatingDeviceTimeCommands,
  HeatingSecondaryCircuitValvesFourThreeWayProperties,
  HeatPumpSmartGridCommands,
  HeatPumpSmartGridProperties,
  HolidayProgramCommands,
  HolidayProgramProperties,
  IsActiveProperty,
  IsEnabledProperty,
  IsNumberValueProperty,
  IsStringValueProperty,
  NumberValue,
  ProgramCommands,
  ProgramProperties,
  PropertiesValue,
  RoomsStatusProperties,
  TypedFeatureProperties,
} from '@vi-iot/client';
import { cloneDeep } from 'lodash-es';

import { Unit } from '../../shared/common/units';
import {
  ExpectedFeatureProperties,
  ExpectedFeaturePropertiesPeriods,
  ExtendedRawConsumptionProperties,
} from '../_models';
import { Indicator } from '../_services';
import { AppState } from '../app.state';
import { RemoteConfigState } from '../remote-config';
import { getWallboxDeviceIndex, sortDevicesInTilesOrder } from './devices.helper';

import { InstallationFeaturesHelpers } from '../installation-features/installation-features.helpers';
import { InstallationFeaturesState } from '../installation-features/installation-features.state';
import { deviceAdapter, DevicesState, RichDevice, srcDeviceVkorgWhitelist } from './devices.state';
import { TrendingCategory } from 'src/app/+installations/installation/trending/trending-chart/trending-chart.helpers';

export namespace DevicesSelectors {
  export const heatingSystemsOverviewUrl =
    'https://systems-overview.viessmann-climatesolutions.com/details';

  export const authState = (state: AppState): AuthState => state.auth;
  export const devicesState = (state: AppState): DevicesState => state.devices;
  export const remoteConfig = (state: AppState): RemoteConfigState => state.remoteConfig;
  export const installationDetails = (state: AppState): InstallationDetailsState =>
    state.installationDetails;
  export const gateway = (state: AppState): GatewayState => state.gateway;
  export const installationFeatures = (state: AppState): InstallationFeaturesState =>
    state.installationFeatures;

  export const {
    selectIds: _selectDeviceIds,
    selectEntities: _selectDeviceEntities,
    selectAll: _selectAllDevices,
    selectTotal: _devicesCount,
  }: EntitySelectors<RichDevice, EntityState<RichDevice>> = deviceAdapter.getSelectors();

  export const stateDevices = createSelector(
    devicesState,
    (_devicesState) => _devicesState.devices
  );

  export const getSelectedDeviceId = (state: DevicesState): string => state.selectedDeviceId;

  export const selectAllDevices = createSelector(stateDevices, _selectAllDevices);
  export const selectDeviceEntities = createSelector(stateDevices, _selectDeviceEntities);
  export const selectCurrentSelectedDeviceId = createSelector(devicesState, getSelectedDeviceId);

  export function selectDeviceIdByDeviceSerial(
    deviceSerial: string
  ): MemoizedSelector<AppState, string | undefined, DefaultProjectorFn<string | undefined>> {
    return createSelector(devicesState, (state: DevicesState) => {
      const foundEntityId = (state.devices.ids as string[]).find(
        (id) => state.devices.entities[id]?.boilerSerial === deviceSerial
      );

      return foundEntityId ? state.devices.entities[foundEntityId].id : undefined;
    });
  }

  export function isFeatureWithPendingUpdates(
    featureName: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(devicesState, (state: DevicesState) => {
      return state.featuresWithPendingUpdates.some((entry) => entry === featureName);
    });
  }

  export const isLoaded: MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > = createSelector(devicesState, (state: DevicesState) => state.devices.ids?.length > 0);

  export const areDevicesLoadedAndOneIsSelected: MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > = createSelector(
    devicesState,
    (state: DevicesState) => state.devices.ids?.length > 0 && state.selectedDeviceId !== null
  );

  export const getSelectedDevice: MemoizedSelector<
    AppState,
    RichDevice | undefined,
    DefaultProjectorFn<RichDevice | undefined>
  > = createSelector(
    selectDeviceEntities,
    selectCurrentSelectedDeviceId,
    (deviceEntities, deviceId) => (deviceId ? deviceEntities[deviceId] : undefined)
  );

  export const selectCurrentDeviceId: MemoizedSelector<
    AppState,
    string | null,
    DefaultProjectorFn<string | null>
  > = createSelector(
    selectDeviceEntities,
    selectCurrentSelectedDeviceId,
    (deviceEntities, deviceId) => deviceEntities[deviceId]?.id ?? null
  );

  export const selectTrendingDevices: MemoizedSelector<
    AppState,
    RichDevice[],
    DefaultProjectorFn<RichDevice[]>
  > = createSelector(getDevicesOfSelectedGateway(), (devices: RichDevice[]) => {
    const trendingDevices = [];

    devices.forEach((device) => {
      const isProduct = device?.roles?.some((role) => role.includes(DeviceRoles.GenericProduct));

      if (isProduct) {
        trendingDevices.push(device);
      }
    });

    return trendingDevices;
  });
  // TODO: This is not main device boiler serial, only selected one
  export const getMainDeviceBoilerSerial: MemoizedSelector<
    AppState,
    string,
    DefaultProjectorFn<string>
  > = createSelector(getSelectedDevice, (dev: RichDevice) => {
    return dev?.boilerSerial;
  });

  export const isVitovalorPA2Device: MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > = createSelector(getSelectedDevice, (dev: RichDevice) => {
    const mandatoryRoles: DeviceRoles[] = [DeviceRoles.FuelCell, DeviceRoles.E3];
    const hasHMUPT2Capability = dev?.roles?.some((role) => role.includes(DeviceCapability.hmuPT2));
    const hasMandatoryRoles = mandatoryRoles.every((role) => dev?.roles?.includes(role));

    return !hasHMUPT2Capability && hasMandatoryRoles;
  });

  export function isFuelCellDevice(
    deviceId?: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      selectAllDevices,
      getSelectedDevice,
      (devicesWithFeatures: RichDevice[], selectedDevice: RichDevice) => {
        const dev: RichDevice = !!deviceId
          ? devicesWithFeatures.find((device) => device.selectId === deviceId)
          : selectedDevice;

        return !!dev?.roles?.includes(DeviceRoles.FuelCell);
      }
    );
  }

  export function isESSDevice(
    deviceId?: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      selectAllDevices,
      getSelectedDevice,
      (devicesWithFeatures: RichDevice[], selectedDevice: RichDevice) => {
        const dev: RichDevice = !!deviceId
          ? devicesWithFeatures.find((device) => device.selectId === deviceId)
          : selectedDevice;

        return !!dev?.roles?.includes(DeviceRoles.Ess);
      }
    );
  }

  export const isEcoWaterDevice: MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > = createSelector(getSelectedDevice, (dev: RichDevice) =>
    dev?.roles.some((role) => role.includes(DeviceRoles.EcoWater))
  );

  export const isEcotronicDevice: MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > = createSelector(getSelectedDevice, (dev: RichDevice) =>
    dev?.roles.some((role) => role.includes(DeviceRoles.Ecotronic))
  );

  export const isStandaloneDevice: MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > = createSelector(getSelectedDevice, (dev: RichDevice) => dev?.isStandalone);

  export function getCurrentlySelectedDevice(): MemoizedSelector<
    AppState,
    RichDevice,
    DefaultProjectorFn<RichDevice>
  > {
    return createSelector(getSelectedDevice, (dev: RichDevice) => dev);
  }

  export function getAvailableCircuitsDetails(
    deviceId: string
  ): MemoizedSelector<AppState, CircuitDetails[], DefaultProjectorFn<CircuitDetails[]>> {
    return createSelector(
      InstallationFeaturesSelectors.installationFeatures,
      ConfigurationSelectors.selectFeaturesBaseUri,
      InstallationDetailsSelectors.getInstallationId(),
      InstallationDetailsSelectors.getGatewaySerial(),
      (
        installationsFeaturesState: InstallationFeaturesState,
        featuresBaseUri: string,
        stateInstallationId: number,
        stateGatewaySerial: string
      ) => {
        const featureUri = InstallationFeaturesHelpers.constructInstallationFeatureUri(
          featuresBaseUri,
          FeatureNames.circuits,
          stateGatewaySerial,
          stateInstallationId,
          deviceId
        );
        const circuits = installationsFeaturesState.entities[featureUri] as Feature<
          IsEnabledProperty,
          never
        >;

        const circuitsPresent = circuits?.properties?.enabled?.value;

        if (!circuitsPresent) {
          return [];
        }
        let availableCircuits: CircuitDetails[] = [];
        if (circuitsPresent) {
          let circuitDetails: Feature<
            CircuitProperty,
            HeatingCircuitCommands
          >[] = circuits?.properties?.enabled?.value.map((circuitId) => {
            const featureCircuitUri = InstallationFeaturesHelpers.constructInstallationFeatureUri(
              featuresBaseUri,
              FeatureNames.circuit(circuitId),
              stateGatewaySerial,
              stateInstallationId,
              deviceId
            );

            return installationsFeaturesState.entities[featureCircuitUri] as Feature<
              CircuitProperty,
              HeatingCircuitCommands
            >;
          });

          const circuitDetailsArray = Object.values(circuitDetails);
          availableCircuits = circuitDetailsArray.map((entry) => {
            const featureSplit = entry.feature.split('.');

            return {
              feature: entry,
              circuitId: featureSplit[featureSplit.length - 1],
            };
          });
        }

        return availableCircuits;
      }
    );
  }

  export function getCallForHeatHeatingZoneIndex(
    deviceId: string
  ): MemoizedSelector<AppState, { index: string }, DefaultProjectorFn<{ index: string }>> {
    return createSelector(getAvailableCircuitsDetails(deviceId), (circuits: CircuitDetails[]) => {
      const ttCircuits = (circuits as any).filter(
        (entry) => entry.feature.properties.type.value === 'ttCircuit'
      );

      const isOnlyCallForHeatHeatingZone = ttCircuits.length === 0;

      if (isOnlyCallForHeatHeatingZone) {
        return { index: null };
      } else {
        const largestTTCircuitZoneIndex = ttCircuits.reduce(function (
          previousValue: number,
          currentEntry
        ) {
          return Math.max(previousValue, +currentEntry.circuitId);
        },
        0);

        // tt index start at 4 and call for heat is 1 index greater than last ttcircuit index
        const ttIndexOffset = 3;
        const callForHeatOffset = 1;
        const callForHeatZoneIndex = largestTTCircuitZoneIndex - ttIndexOffset + callForHeatOffset;

        return { index: `${callForHeatZoneIndex}` };
      }
    });
  }

  export function isHolidayProgramOrHolidayAtHomeActive(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      InstallationFeaturesSelectors.installationFeatures,
      ConfigurationSelectors.selectFeaturesBaseUri,
      InstallationDetailsSelectors.getInstallationId(),
      InstallationDetailsSelectors.getSelectedGateway(),
      DevicesSelectors.getSelectedDevice,
      (
        installationFeaturesState: InstallationFeaturesState,
        featuresBaseUri: string,
        stateInstallationId: number,
        stateGatewaySerial: Gateway,
        stateDevice: RichDevice
      ) => {
        const circuitsUri = InstallationFeaturesHelpers.constructInstallationFeatureUri(
          featuresBaseUri,
          FeatureNames.circuits,
          stateGatewaySerial.serial,
          stateInstallationId,
          stateDevice.id
        );
        const circuitsFeature = installationFeaturesState.entities[circuitsUri] as Feature<
          IsEnabledProperty,
          never
        >;

        const circuitIds = circuitsFeature?.properties?.enabled?.value || [];
        let result = false;
        circuitIds.forEach((circuitId: string) => {
          const HolidayProgramCircuitIdUri = InstallationFeaturesHelpers.constructInstallationFeatureUri(
            featuresBaseUri,
            FeatureNames.circuitHolidayProgram(circuitId),
            stateGatewaySerial.serial,
            stateInstallationId,
            stateDevice.id
          );
          const holidayProgramFeature = installationFeaturesState.entities[
            HolidayProgramCircuitIdUri
          ] as Feature<HolidayProgramProperties, HolidayProgramCommands>;

          const HolidayAtHomeCircuitIdUri = InstallationFeaturesHelpers.constructInstallationFeatureUri(
            featuresBaseUri,
            FeatureNames.circuitHolidayAtHome(circuitId),
            stateGatewaySerial.serial,
            stateInstallationId,
            stateDevice.id
          );

          const holidayAtHomeFeature = installationFeaturesState.entities[
            HolidayAtHomeCircuitIdUri
          ] as Feature<HolidayProgramProperties, HolidayProgramCommands>;

          if (
            (isReady(holidayProgramFeature) || isReady(holidayAtHomeFeature)) &&
            (!!holidayProgramFeature?.properties?.start?.value ||
              !!holidayAtHomeFeature?.properties?.start?.value)
          ) {
            result = true;
          }
        });

        return result;
      }
    );
  }

  export function isMultiFamily(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationDeviceFeature(
        FeatureNames.configurationMultiFamilyHouse
      ),
      (isMultiFamily: Feature<IsActiveProperty, never>) =>
        !!isMultiFamily?.properties?.active?.value
    );
  }

  export function getIndicators(): MemoizedSelector<
    AppState,
    Indicator,
    DefaultProjectorFn<Indicator>
  > {
    return createSelector(getSelectedDevice, (dev: RichDevice) => ({
      loading: dev?.loading,
      loaded: dev?.loaded,
      error: dev?.error,
    }));
  }

  export function isSystemPanelVisible(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      InstallationDetailsSelectors.getSelectedGateway(),
      DevicesSelectors.isStandaloneDevice,
      (selectedGateway, isStandalone) => {
        const gatewayStatus: AggregatedStatus = selectedGateway?.aggregatedStatus;
        const isGatewayOnline: boolean =
          gatewayStatus === AggregatedStatus.WorksProperly ||
          gatewayStatus === AggregatedStatus.Error ||
          gatewayStatus === AggregatedStatus.Maintenance;

        return isGatewayOnline && !isStandalone;
      }
    );
  }

  export function isFloorHeatingThermostatAvailable(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      remoteConfig,
      (remoteConfigState: RemoteConfigState) =>
        !!remoteConfigState?.remoteConfig?.floorHeatingThermostatAvailable
    );
  }

  export function isDhwAvailable(): MemoizedSelector<
    AppState,
    { isDhw: boolean },
    DefaultProjectorFn<{ isDhw: boolean }>
  > {
    return createSelector(
      InstallationFeaturesSelectors.installationFeatures,
      ConfigurationSelectors.selectFeaturesBaseUri,
      InstallationDetailsSelectors.getInstallationId(),
      InstallationDetailsSelectors.getGatewaySerial(),
      DevicesSelectors.selectCurrentDeviceId,
      (
        installationsFeaturesState: InstallationFeaturesState,
        featuresBaseUri: string,
        stateInstallationId: number,
        stateGatewaySerial: string,
        currentDeviceId: string
      ) => {
        return Helpers.isDhwActivated(
          installationsFeaturesState,
          featuresBaseUri,
          stateInstallationId,
          stateGatewaySerial,
          currentDeviceId
        );
      }
    );
  }

  export function isDhwOneTimeChargeEnabled(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationDeviceFeature(FeatureNames.dhwOneTimeCharge),
      (dhwOneTimeCharge: Feature<IsActiveProperty, ActivateDeactivateCommands>) => {
        const canExecuteCommand: boolean = dhwOneTimeCharge.properties?.active?.value
          ? dhwOneTimeCharge.commands?.deactivate?.isExecutable
          : dhwOneTimeCharge.commands?.activate?.isExecutable;

        return canExecuteCommand;
      }
    );
  }

  export function isHeatingZoneAvailable(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationDeviceFeature(FeatureNames.callForHeatDemand),
      InstallationFeaturesSelectors.selectInstallationDeviceFeature(
        FeatureNames.callForHeatConfigurationRegulation
      ),
      InstallationFeaturesSelectors.selectInstallationDeviceFeature(
        FeatureNames.callForHeatHeatingCurve
      ),
      InstallationFeaturesSelectors.selectInstallationDeviceFeature(
        FeatureNames.callForHeatHeatingCurvePresets
      ),
      InstallationFeaturesSelectors.selectInstallationDeviceFeature(FeatureNames.callForHeatPump),
      InstallationFeaturesSelectors.selectInstallationDeviceFeature(
        FeatureNames.callForHeatSensorsTemperature
      ),
      (
        heatDemandFeature: Feature<CallForHeatDemandProperties, never>,
        heatConfigurationRegulationFeature: Feature<
          HeatingConfigurationRegulationProperties,
          never
        >,
        heatingCurveFeature: Feature<IsStringValueProperty, never>,
        heatingCurvePresetsFeature: Feature<IsStringValueProperty, never>,
        heatPumpFeature: Feature<IsActiveProperty, never>,
        heatSensorsTemperatureFeature: Feature<DcSensorValueProperties, never>
      ) => {
        return (
          isReady(heatDemandFeature) ||
          isReady(heatConfigurationRegulationFeature) ||
          isReady(heatingCurveFeature) ||
          isReady(heatingCurvePresetsFeature) ||
          isReady(heatPumpFeature) ||
          isReady(heatSensorsTemperatureFeature)
        );
      }
    );
  }

  export function getAutarky(
    deviceId: string = null
  ): MemoizedSelector<
    AppState,
    ExtendedRawConsumptionProperties[],
    DefaultProjectorFn<ExtendedRawConsumptionProperties[]>
  > {
    const consumptionColorClasses: string[] = ['vi-pending-07'];

    const expectedFeatureProperties: ExpectedFeatureProperties[] = [
      {
        name: FeatureNames.heatingPowerProductionDemandCoverageRaw,
        designationKey: 'HEATING.POWER.PRODUCTION.DEMANDCOVERAGE.RAW',
      },
    ];

    const expectedFeaturePropertiesPeriods: ExpectedFeaturePropertiesPeriods[] = [
      {
        name: 'day',
        trimSamples: 9,
      },
      // Week values are dynamic, meaning week values retrieved from the backend may vary in length depending whether device is legacy or E3
      // {
      //   name: 'week',
      //   trimSamples: ???,
      // },
      {
        name: 'month',
        trimSamples: 13,
      },
      {
        name: 'year',
        trimSamples: 13,
      },
    ];

    return _createStatisticsSelector(
      deviceId,
      expectedFeatureProperties,
      consumptionColorClasses,
      expectedFeaturePropertiesPeriods
    );
  }

  export function getPowerConsumption(
    deviceId: string = null
  ): MemoizedSelector<
    AppState,
    ExtendedRawConsumptionProperties[],
    DefaultProjectorFn<ExtendedRawConsumptionProperties[]>
  > {
    const consumptionColorClasses: string[] = [
      'vi-pending-07',
      'vi-pending-05',
      'vi-pending-03',
      'vi-pending-02',
      'vi-pending-01',
    ];

    const expectedFeatureProperties: ExpectedFeatureProperties[] = [
      {
        name: FeatureNames.powerConsumptionDhw,
        designationKey: 'FEATURE.HEATING.POWER.CONSUMPTION.DHW',
      },
      {
        name: FeatureNames.powerConsumptionHeating,
        designationKey: 'FEATURE.HEATING.POWER.CONSUMPTION.HEATING',
      },
      {
        name: FeatureNames.powerConsumptionCooling,
        designationKey: 'FEATURE.HEATING.POWER.CONSUMPTION.COOLING',
      },
    ];

    const expectedFeaturePropertiesPeriods: ExpectedFeaturePropertiesPeriods[] = [
      {
        name: 'day',
        trimSamples: 9,
      },
      // Week values are dynamic, meaning week values retrieved from the backend may vary in length depending whether device is legacy or E3
      // {
      //   name: 'week',
      //   trimSamples: ???,
      // },
      {
        name: 'month',
        trimSamples: 13,
      },
      {
        name: 'year',
        trimSamples: 13,
      },
    ];

    return _createStatisticsSelector(
      deviceId,
      expectedFeatureProperties,
      consumptionColorClasses,
      expectedFeaturePropertiesPeriods,
      FeatureNames.powerConsumptionTotal,
      'FEATURE.HEATING.POWER.CONSUMPTION.TOTAL'
    );
  }
  export function getDetailedPowerConsumption(
    deviceId: string = null
  ): MemoizedSelector<
    AppState,
    ExtendedRawConsumptionProperties[],
    DefaultProjectorFn<ExtendedRawConsumptionProperties[]>
  > {
    const consumptionColorClasses: string[] = [
      'vi-pending-08',
      'vi-pending-06',
      'vi-pending-04',
      'vi-pending-02',
      'vi-pending-01',
    ];

    const expectedFeatureProperties: ExpectedFeatureProperties[] = [
      {
        name: FeatureNames.powerCompressorConsumptionDhw,
        designationKey: 'FEATURE.HEATING.COMPRESSORS.0.POWER.CONSUMPTION.DHW',
      },
      {
        name: FeatureNames.powerElectricalConsumptionDhw,
        designationKey: 'FEATURE.HEATING.HEATINGROD.POWER.CONSUMPTION.DHW',
      },
      {
        name: FeatureNames.powerCompressorConsumptionHeating,
        designationKey: 'FEATURE.HEATING.COMPRESSORS.0.POWER.CONSUMPTION.HEATING',
      },
      {
        name: FeatureNames.powerElectricalConsumptionHeating,
        designationKey: 'FEATURE.HEATING.HEATINGROD.POWER.CONSUMPTION.HEATING',
      },
      {
        name: FeatureNames.powerConsumptionCooling,
        designationKey: 'FEATURE.HEATING.POWER.CONSUMPTION.COOLING',
      },
    ];

    const expectedFeaturePropertiesPeriods: ExpectedFeaturePropertiesPeriods[] = [
      {
        name: 'day',
        trimSamples: 9,
      },
      // Week values are dynamic, meaning week values retrieved from the backend may vary in length depending whether device is legacy or E3
      // {
      //   name: 'week',
      //   trimSamples: ???,
      // },
      {
        name: 'month',
        trimSamples: 13,
      },
      {
        name: 'year',
        trimSamples: 13,
      },
    ];

    return _createStatisticsSelector(
      deviceId,
      expectedFeatureProperties,
      consumptionColorClasses,
      expectedFeaturePropertiesPeriods,
      FeatureNames.powerConsumptionTotal,
      'FEATURE.HEATING.POWER.CONSUMPTION.TOTAL'
    );
  }

  export function getProducedHeat(
    deviceId: string = null
  ): MemoizedSelector<
    AppState,
    ExtendedRawConsumptionProperties[],
    DefaultProjectorFn<ExtendedRawConsumptionProperties[]>
  > {
    const consumptionColorClasses: string[] = [
      'vi-pending-07',
      'vi-pending-05',
      'vi-pending-03',
      'vi-pending-02',
    ];

    const expectedFeatureProperties: ExpectedFeatureProperties[] = [
      {
        name: FeatureNames.heatingProductionDhw,
        designationKey: 'FEATURE.HEATING.HEAT.PRODUCTION.DHW',
      },
      {
        name: FeatureNames.heatingProductionHeating,
        designationKey: 'FEATURE.HEATING.HEAT.PRODUCTION.HEATING',
      },
      {
        name: FeatureNames.heatingProductionCooling,
        designationKey: 'FEATURE.HEATING.HEAT.PRODUCTION.COOLING',
      },
    ];

    const expectedFeaturePropertiesPeriods: ExpectedFeaturePropertiesPeriods[] = [
      {
        name: 'day',
        trimSamples: 9,
      },
      // Week values are dynamic, meaning week values retrieved from the backend may vary in length depending whether device is legacy or E3
      // {
      //   name: 'week',
      //   trimSamples: ???,
      // },
      {
        name: 'month',
        trimSamples: 13,
      },
      {
        name: 'year',
        trimSamples: 13,
      },
    ];

    return _createStatisticsSelector(
      deviceId,
      expectedFeatureProperties,
      consumptionColorClasses,
      expectedFeaturePropertiesPeriods,
      FeatureNames.heatingProductionTotal,
      'FEATURE.HEATING.HEAT.PRODUCTION.TOTAL'
    );
  }

  export function getGasConsumption(
    deviceId: string = null
  ): MemoizedSelector<
    AppState,
    ExtendedRawConsumptionProperties[],
    DefaultProjectorFn<ExtendedRawConsumptionProperties[]>
  > {
    const consumptionColorClasses: string[] = ['vi-pending-07', 'vi-pending-05', 'vi-pending-03'];

    const expectedFeatureProperties: ExpectedFeatureProperties[] = [
      {
        name: FeatureNames.gasConsumptionDhw,
        designationKey: 'FEATURE.HEATING.GAS.CONSUMPTION.DHW',
      },
      {
        name: FeatureNames.gasConsumptionHeating,
        designationKey: 'FEATURE.HEATING.GAS.CONSUMPTION.HEATING',
      },
      {
        name: FeatureNames.gasConsumptionFuelCell,
        designationKey: 'FEATURE.HEATING.GAS.CONSUMPTION.FUELLCELL',
      },
    ];

    const expectedFeaturePropertiesPeriods: ExpectedFeaturePropertiesPeriods[] = [
      {
        name: 'day',
        trimSamples: 9,
      },
      // Week values are dynamic, meaning week values retrieved from the backend may vary in length depending whether device is legacy or E3
      // {
      //   name: 'week',
      //   trimSamples: ???,
      // },
      {
        name: 'month',
        trimSamples: 13,
      },
      {
        name: 'year',
        trimSamples: 13,
      },
    ];

    return _createStatisticsSelector(
      deviceId,
      expectedFeatureProperties,
      consumptionColorClasses,
      expectedFeaturePropertiesPeriods,
      FeatureNames.gasConsumptionTotal,
      'FEATURE.HEATING.GAS.CONSUMPTION.TOTAL'
    );
  }

  export function getSolarPowerProduction(
    deviceId: string = null
  ): MemoizedSelector<
    AppState,
    ExtendedRawConsumptionProperties[],
    DefaultProjectorFn<ExtendedRawConsumptionProperties[]>
  > {
    const consumptionColorClasses: string[] = ['vi-pending-07'];

    const expectedFeatureProperties: ExpectedFeatureProperties[] = [
      {
        name: FeatureNames.solarPowerProduction,
        designationKey: 'FEATURE.HEATING.SOLAR.POWER.PRODUCTION',
      },
    ];

    const expectedFeaturePropertiesPeriods: ExpectedFeaturePropertiesPeriods[] = [
      {
        name: 'day',
        trimSamples: 9,
      },
      // Week values are dynamic, meaning week values retrieved from the backend may vary in length depending whether device is legacy or E3
      // {
      //   name: 'week',
      //   trimSamples: ???,
      // },
      {
        name: 'month',
        trimSamples: 13,
      },
      {
        name: 'year',
        trimSamples: 13,
      },
    ];

    return _createStatisticsSelector(
      deviceId,
      expectedFeatureProperties,
      consumptionColorClasses,
      expectedFeaturePropertiesPeriods
    );
  }

  export function getPowerProduction(
    deviceId: string = null
  ): MemoizedSelector<
    AppState,
    ExtendedRawConsumptionProperties[],
    DefaultProjectorFn<ExtendedRawConsumptionProperties[]>
  > {
    const consumptionColorClasses: string[] = ['vi-pending-07'];

    const expectedFeatureProperties: ExpectedFeatureProperties[] = [
      {
        name: FeatureNames.fuelCellPowerProduction,
        designationKey: 'FEATURE.HEATING.POWER.POWER_PRODUCTION_CHART',
      },
    ];

    const expectedFeaturePropertiesPeriods: ExpectedFeaturePropertiesPeriods[] = [
      {
        name: 'day',
        trimSamples: 9,
      },
      // Week values are dynamic, meaning week values retrieved from the backend may vary in length depending whether device is legacy or E3
      // {
      //   name: 'week',
      //   trimSamples: ???,
      // },
      {
        name: 'month',
        trimSamples: 13,
      },
      {
        name: 'year',
        trimSamples: 13,
      },
    ];

    return _createStatisticsSelector(
      deviceId,
      expectedFeatureProperties,
      consumptionColorClasses,
      expectedFeaturePropertiesPeriods
    );
  }

  function _createStatisticsSelector(
    deviceId: string,
    expectedFeatureProperties: ExpectedFeatureProperties[],
    statisticsColorClasses: string[],
    expectedFeaturePropertiesPeriods: ExpectedFeaturePropertiesPeriods[],
    featureNameTotal?: string,
    featureNameTotalDesignationKey?: string
  ): MemoizedSelector<
    AppState,
    ExtendedRawConsumptionProperties[],
    DefaultProjectorFn<ExtendedRawConsumptionProperties[]>
  > {
    return createSelector(
      InstallationFeaturesSelectors.installationFeatures,
      ConfigurationSelectors.selectFeaturesBaseUri,
      InstallationDetailsSelectors.getInstallationId(),
      InstallationDetailsSelectors.getGatewaySerial(),
      DevicesSelectors.getSelectedDevice,
      (
        installationsFeaturesState: InstallationFeaturesState,
        featuresBaseUri: string,
        stateInstallationId: number,
        stateGatewaySerial: string,
        stateDevice: RichDevice
      ) => {
        const properties: ExtendedRawConsumptionProperties[] = [];

        expectedFeatureProperties.forEach((expected, index) => {
          const featureUri = InstallationFeaturesHelpers.constructInstallationFeatureUri(
            featuresBaseUri,
            expected.name,
            stateGatewaySerial,
            stateInstallationId,
            deviceId ?? stateDevice.id
          );
          const feature = installationsFeaturesState.entities[featureUri];
          const statisticsFeature = cloneDeep(
            _getFeature(
              feature,
              statisticsColorClasses[index],
              expected.designationKey,
              expectedFeaturePropertiesPeriods
            )
          );

          if (statisticsFeature) {
            // VIG-5940: If standalone unit is missing take value-property unit
            if (!statisticsFeature.unit) {
              statisticsFeature.unit = {
                value: statisticsFeature.day.unit,
                type: DcTypes.string,
              };
            }
            properties.push(statisticsFeature);
          }
        });

        if (featureNameTotal && featureNameTotalDesignationKey) {
          // get and display total only if we have more than 1 series
          const totalFeatureUri = InstallationFeaturesHelpers.constructInstallationFeatureUri(
            featuresBaseUri,
            featureNameTotal,
            stateGatewaySerial,
            stateInstallationId,
            stateDevice ? stateDevice.id : deviceId
          );
          const totalFeature = installationsFeaturesState.entities[totalFeatureUri];
          const total = _getTotal(
            totalFeature,
            properties,
            featureNameTotalDesignationKey,
            expectedFeaturePropertiesPeriods
          );

          if (total) {
            // VIG-5940: If standalone unit is missing take value-property unit
            if (!total.unit) {
              total.unit = {
                value: total.day.unit,
                type: DcTypes.string,
              };
            }
            properties.push(total);
          }
        }

        return _sameUnitCheck(properties) ? properties : [];
      }
    );
  }

  function _getFeature(
    feature: Feature<unknown, unknown>,
    assignColor: string,
    assignDesignationKey: string,
    expectedFeaturePropertiesPeriods: ExpectedFeaturePropertiesPeriods[]
  ): ExtendedRawConsumptionProperties | null {
    if (isReady(feature)) {
      feature.properties['color'] = assignColor;
      feature.properties['designationKey'] = assignDesignationKey;
      expectedFeaturePropertiesPeriods.forEach((period) => {
        const periodProp = feature.properties[period.name];
        if (periodProp && periodProp.value) {
          periodProp.value = periodProp.value.slice(0, period.trimSamples);
        }
      });

      return feature.properties as ExtendedRawConsumptionProperties;
    } else {
      return null;
    }
  }

  function _sameUnitCheck(properties: ExtendedRawConsumptionProperties[]): boolean {
    // there is no way to calculate m^3 to kWh or the opposite => we should display the feature only if all series have the same unit
    let units = [];

    // get all units of all properties
    properties.forEach((property) => {
      for (const p in property) {
        if (property[p]?.unit) {
          units.push(property[p].unit);
        }
      }
    });

    const distinctUnit = [...new Map(units.map((item) => [item['unit'], item])).values()];

    return distinctUnit.length === 1;
  }

  function _calculateTotalValuesForAvailableFeatures(
    properties: ExtendedRawConsumptionProperties[],
    expectedPeriods: string[],
    totalDesignationKey: string
  ): ExtendedRawConsumptionProperties {
    const total: ExtendedRawConsumptionProperties = {
      day: {
        value: null,
        type: DcTypes.array,
      },
      week: {
        value: null,
        type: DcTypes.array,
      },
      month: {
        value: null,
        type: DcTypes.array,
      },
      year: {
        value: null,
        type: DcTypes.array,
      },
      unit: properties[0].unit,
      color: null,
      designationKey: totalDesignationKey,
    };

    expectedPeriods.forEach((periodName) => {
      const valuesWithinSeries: number[][] = [];
      properties.forEach((values) => {
        if (values[periodName]) {
          valuesWithinSeries.push(values[periodName].value);
        }
      });
      const totalValues = sumArrays(...valuesWithinSeries);
      total[periodName].value = totalValues;
    });

    return total;
  }

  function _getTotal(
    feature: Feature<unknown, unknown>,
    properties: ExtendedRawConsumptionProperties[],
    totalDesignationKey: string,
    expectedFeaturePropertiesPeriods: ExpectedFeaturePropertiesPeriods[]
  ): ExtendedRawConsumptionProperties {
    if (properties.length > 1) {
      const consumptionTotal = _getFeature(
        feature,
        null,
        totalDesignationKey,
        expectedFeaturePropertiesPeriods
      );
      if (consumptionTotal) {
        return consumptionTotal;
      } else {
        const expectedPeriods = expectedFeaturePropertiesPeriods.map((x) => x.name);

        return _calculateTotalValuesForAvailableFeatures(
          properties,
          expectedPeriods,
          totalDesignationKey
        );
      }
    }
  }

  export function isStandaloneGateway(
    gatewayDevicesFeature: Feature<GatewayDevicesProperties, never>
  ): boolean {
    const gatewayDevices: GatewayDevice[] = gatewayDevicesFeature?.properties?.devices?.value || [];

    const isRoomControlDevice = gatewayDevices.some((dev) => dev.type === DeviceTypes.RoomControl);
    const isHeatingDevice = gatewayDevices.some((dev) => dev.type === DeviceTypes.Heating);

    return isRoomControlDevice && !isHeatingDevice;
  }

  export function getDevicesOfSelectedGateway(): MemoizedSelector<
    AppState,
    RichDevice[],
    DefaultProjectorFn<RichDevice[]>
  > {
    return createSelector(
      selectAllDevices,
      DevicesSelectors.getSelectedDevice,
      (devicesWithFeatures: RichDevice[], selectedDevice: RichDevice) =>
        devicesWithFeatures?.filter((dev) => dev.gatewaySerial === selectedDevice?.gatewaySerial)
    );
  }

  export const selectDeviceIdToMemberIdMap: MemoizedSelector<
    AppState,
    Record<string, number>,
    DefaultProjectorFn<Record<string, number>>
  > = createSelector(
    installationFeatures,
    (state): Record<string, number> => {
      const deviceIdToMemberIdMap: Record<string, number> = {};

      for (const uri in state.entities) {
        const feature = state.entities[uri] as Feature<PropertiesValue<NumberValue>, never>;
        if (feature?.feature === FeatureNames.cascadeMemberId) {
          const deviceId = feature.deviceId;
          deviceIdToMemberIdMap[deviceId] = feature.properties.value.value;
        }
      }

      return deviceIdToMemberIdMap;
    }
  );

  export const selectIsFullCascadeMemberIds: MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > = createSelector(
    selectAllDevices,
    DevicesSelectors.selectDeviceIdToMemberIdMap,
    DevicesSelectors.getSelectedDevice,
    (devicesWithFeatures, deviceIdToMemberIdMap, selectedDevice): boolean => {
      const gatewaySerial = selectedDevice?.gatewaySerial;

      const allDevices = devicesWithFeatures?.filter(
        (dev) =>
          dev.gatewaySerial === gatewaySerial &&
          (dev.roles.includes(DeviceRoles.CascadeV2Lead) ||
            dev.roles.includes(DeviceRoles.CascadeV2Lag))
      );

      return (
        allDevices.length > 0 &&
        allDevices.every((dev) => deviceIdToMemberIdMap[dev.id] !== undefined)
      );
    }
  );

  export function getDevicesOfV2Cascade(
    topDevicesLimit: number = null,
    deviceGatewaySerial: string = null
  ): MemoizedSelector<AppState, RichDevice[], DefaultProjectorFn<RichDevice[]>> {
    return createSelector(
      selectAllDevices,
      DevicesSelectors.getSelectedDevice,
      DevicesSelectors.selectDeviceIdToMemberIdMap,
      (
        devicesWithFeatures: RichDevice[],
        selectedDevice: RichDevice,
        deviceIdToMemberIdMap: Record<string, number>
      ) => {
        const gatewaySerial = deviceGatewaySerial ?? selectedDevice?.gatewaySerial;

        const allDevices: RichDevice[] = devicesWithFeatures?.filter(
          (dev) =>
            dev.gatewaySerial === gatewaySerial &&
            (dev.roles.includes(DeviceRoles.CascadeV2Lead) ||
              dev.roles.includes(DeviceRoles.CascadeV2Lag))
        );
        const hasMissingMemberId = allDevices.some(
          (dev) => deviceIdToMemberIdMap[dev.id] === undefined
        );

        if (hasMissingMemberId) {
          return topDevicesLimit === null ? allDevices : allDevices.slice(0, topDevicesLimit);
        }

        const enrichedDevices = allDevices.map((dev) => ({
          ...dev,
          memberId: deviceIdToMemberIdMap[dev.id] - 1,
        }));
        const sortedDevices = enrichedDevices.sort((a, b) => a.memberId - b.memberId);

        return topDevicesLimit === null ? sortedDevices : sortedDevices.slice(0, topDevicesLimit);
      }
    );
  }

  export function getDevicesIdsOfV2Cascade(): MemoizedSelector<
    AppState,
    string[],
    DefaultProjectorFn<string[]>
  > {
    return createSelector(
      selectAllDevices,
      DevicesSelectors.getSelectedDevice,
      (devicesWithFeatures: RichDevice[], selectedDevice: RichDevice) => {
        const gatewaySerial = selectedDevice?.gatewaySerial;

        const allDevices: RichDevice[] = devicesWithFeatures?.filter(
          (dev) =>
            dev.gatewaySerial === gatewaySerial &&
            (dev.roles.includes(DeviceRoles.CascadeV2Lead) ||
              dev.roles.includes(DeviceRoles.CascadeV2Lag))
        );

        return allDevices.map((device) => device.id);
      }
    );
  }

  export function getDevicesIdsOfVitoCharge(): MemoizedSelector<
    AppState,
    string[],
    DefaultProjectorFn<string[]>
  > {
    return createSelector(
      selectAllDevices,
      DevicesSelectors.getSelectedDevice,
      (devicesWithFeatures: RichDevice[], selectedDevice: RichDevice) => {
        const gatewaySerial = selectedDevice?.gatewaySerial;

        const allDevices: RichDevice[] = devicesWithFeatures?.filter(
          (dev) =>
            dev.gatewaySerial === gatewaySerial && dev.roles.includes(DeviceRoles.ProductVitocharge)
        );

        return allDevices.map((device) => device.id);
      }
    );
  }

  export function getFhtDeviceChannels(
    fhtId: string
  ): MemoizedSelector<AppState, RichDevice[], DefaultProjectorFn<RichDevice[]>> {
    return createSelector(selectAllDevices, (devicesWithFeatures: RichDevice[]) => {
      const fhtChannels = devicesWithFeatures.filter((entry) => {
        return entry.roles.includes(DeviceRoles.FhtChannel) && entry.id.includes(fhtId);
      });

      return fhtChannels;
    });
  }

  export function isV2Cascade(): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      getDevicesOfV2Cascade(),
      (devicesWithFeatures: RichDevice[]) => !!devicesWithFeatures?.length
    );
  }

  export function getRemoteControllerDevices(): MemoizedSelector<
    AppState,
    RichDevice[],
    DefaultProjectorFn<RichDevice[]>
  > {
    return createSelector(selectAllDevices, (devicesWithFeatures: RichDevice[]) =>
      devicesWithFeatures?.filter((dev) => {
        return [
          DeviceRoles.RemoteControllerVitotrol100E,
          DeviceRoles.RemoteControllerVitotrol300E,
        ].some((role) => dev?.roles?.includes(role));
      })
    );
  }

  export function isRemoteControllerAvailable(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      DevicesSelectors.getRemoteControllerDevices(),
      InstallationDetailsSelectors.getGatewaySerial(),
      (remoteControllerDevices: RichDevice[], gatewaySerial: string): boolean =>
        remoteControllerDevices.some((device) => device.gatewaySerial === gatewaySerial)
    );
  }

  export function getMainDevices(): MemoizedSelector<
    AppState,
    RichDevice[],
    DefaultProjectorFn<RichDevice[]>
  > {
    return createSelector(selectAllDevices, (devicesWithFeatures: RichDevice[]) =>
      devicesWithFeatures?.filter(
        (dev: RichDevice) => dev?.isMainDeviceOfGateway || dev?.isStandalone
      )
    );
  }

  export function getHeatingDevice(): MemoizedSelector<
    AppState,
    RichDevice,
    DefaultProjectorFn<RichDevice>
  > {
    return createSelector(
      getSelectedDevice,
      getMainDevices(),
      (selectedDevice: RichDevice, devicesWithFeatures: RichDevice[]) => {
        return selectedDevice?.deviceType === DeviceTypes.Heating
          ? selectedDevice
          : devicesWithFeatures?.filter(
              (dev: RichDevice) => dev?.deviceType === DeviceTypes.Heating
            )[0];
      }
    );
  }

  export function getDeviceByType(
    deviceType: DeviceTypes
  ): MemoizedSelector<AppState, RichDevice, DefaultProjectorFn<RichDevice>> {
    return createSelector(
      getSelectedDevice,
      selectAllDevices,
      (selectedDevice: RichDevice, devicesWithFeatures: RichDevice[]) => {
        return selectedDevice?.deviceType === deviceType
          ? selectedDevice
          : devicesWithFeatures?.filter((dev: RichDevice) => dev?.deviceType === deviceType)[0];
      }
    );
  }

  export function getMainAndLagDevices(): MemoizedSelector<
    AppState,
    RichDevice[],
    DefaultProjectorFn<RichDevice[]>
  > {
    return createSelector(selectAllDevices, (devicesWithFeatures: RichDevice[]) =>
      devicesWithFeatures?.filter(
        (dev: RichDevice) =>
          dev.isMainDeviceOfGateway ||
          dev.isStandalone ||
          dev.roles.includes(DeviceRoles.CascadeV2Lag)
      )
    );
  }

  export function getV1CascadeLeadDevice(): MemoizedSelector<
    AppState,
    RichDevice,
    DefaultProjectorFn<RichDevice>
  > {
    return createSelector(
      selectAllDevices,
      getSelectedDevice,
      InstallationDetailsSelectors.getSelectedGateway(),
      (devicesWithFeatures: RichDevice[], selectedDevice: RichDevice, selectedGateway: Gateway) => {
        const leadDevice: RichDevice = devicesWithFeatures.find(
          (dev: RichDevice) =>
            dev.isLeadDeviceInCascades && dev.gatewaySerial === selectedGateway.serial
        );

        // standalone case - no multiple gateway support as the requirements are not clear - so we pick 1 device on the 1 gateway
        // Details https://jira.viessmann-climatesolutions.com/browse/VIG-3234
        return leadDevice ?? selectedDevice;
      }
    );
  }

  export function isE3HeatpumpDevice(
    dev?: RichDevice
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(selectAllDevices, (devicesWithFeatures: RichDevice[]) => {
      const mandatoryRoles: DeviceRoles[] = [DeviceRoles.Heatpump, DeviceRoles.E3];
      if (dev) {
        return mandatoryRoles.every((role) => dev.roles?.includes(role));
      } else {
        return devicesWithFeatures?.some((device: RichDevice) =>
          mandatoryRoles.every((role) => device.roles?.includes(role))
        );
      }
    });
  }

  export function isE3DHWHeatpumpDevice(
    dev?: RichDevice
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(selectAllDevices, (devicesWithFeatures: RichDevice[]) => {
      const mandatoryRoles: DeviceRoles[] = [DeviceRoles.DHWHeatpump, DeviceRoles.E3];
      if (dev) {
        return mandatoryRoles.every((role) => dev.roles?.includes(role));
      } else {
        return devicesWithFeatures?.some((device: RichDevice) =>
          mandatoryRoles.every((role) => device.roles?.includes(role))
        );
      }
    });
  }

  export function isE3Device(): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(selectAllDevices, (devicesWithFeatures: RichDevice[]) =>
      devicesWithFeatures?.some((device: RichDevice) => device.roles?.includes(DeviceRoles.E3))
    );
  }

  export function getHeatingSystemOverviewUrl(): MemoizedSelector<
    AppState,
    string,
    DefaultProjectorFn<string>
  > {
    return createSelector(getSelectedDevice, (selectedDevice: RichDevice) => {
      const equipmentNumber = selectedDevice?.equipmentNumber;

      if (equipmentNumber) {
        return `${this.heatingSystemsOverviewUrl}/${equipmentNumber}`;
      }

      return '';
    });
  }

  export function getBatteryName(
    id: number
  ): MemoizedSelector<AppState, string, DefaultProjectorFn<string>> {
    return createSelector(
      getSelectedDevice,
      InstallationFeaturesSelectors.selectInstallationDeviceFeature(
        FeatureNames.batteryInternalComponents
      ),
      (
        device: RichDevice,
        batteryInternalComponentsFeature: Feature<BatteryInternalComponentsProperties, never>
      ) => {
        const serialNumber: string =
          batteryInternalComponentsFeature?.properties?.vinList?.value[id];
        const nameBySerialNumber: string = getBatteryNameBySerial(serialNumber);

        return nameBySerialNumber || device?.marketingName || '';
      }
    );
  }

  export function getBatteryInformation(
    id: number,
    param: BatteryInformationFeaturesType
  ): MemoizedSelector<AppState, NumberValue, DefaultProjectorFn<NumberValue>> {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationDeviceFeature(
        FeatureNames.batteryInformation(id.toString())
      ),
      (batteryInformationFeature: Feature<BatteryInformationFeatures, never>) => {
        const batteryInformation = batteryInformationFeature?.properties[param] ?? null;

        return batteryInformation;
      }
    );
  }

  export function getNormalProgramTemperature(
    circuitId: string
  ): MemoizedSelector<AppState, number, DefaultProjectorFn<number>> {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationDeviceFeature(
        FeatureNames.circuitOperatingProgram(circuitId, NormalOperatingProgramType.NormalHeating)
      ),
      InstallationFeaturesSelectors.selectInstallationDeviceFeature(
        FeatureNames.circuitOperatingProgram(circuitId, NormalOperatingProgramType.Normal)
      ),
      DevicesSelectors.isE3HeatpumpDevice(),
      (
        circuitOperatingNormalHeatingProgramFeature: Feature<ProgramProperties, ProgramCommands>,
        circuitOperatingNormalProgramFeature: Feature<ProgramProperties, ProgramCommands>,
        isE3Dev: boolean
      ) => {
        const feature: Feature<ProgramProperties, ProgramCommands> = isE3Dev
          ? circuitOperatingNormalHeatingProgramFeature
          : circuitOperatingNormalProgramFeature;

        const fallbackTemperature = 20;
        const normalTemperature: number = feature?.properties?.temperature?.value;

        return normalTemperature ?? fallbackTemperature;
      }
    );
  }

  export function getHeatPumpSmartGridActualState(): MemoizedSelector<
    AppState,
    string,
    DefaultProjectorFn<string>
  > {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationDeviceFeature(FeatureNames.heatPumpSmartGrid),
      DevicesSelectors.isE3HeatpumpDevice(),
      DevicesSelectors.isE3DHWHeatpumpDevice(),
      (
        heatPumpSmartGridFeatures: Feature<HeatPumpSmartGridProperties, HeatPumpSmartGridCommands>,
        isE3HeatPump: boolean,
        isE3DHWHeatpump: boolean
      ): string => {
        if (!isE3HeatPump && !isE3DHWHeatpump) {
          return null;
        }

        return isReady(heatPumpSmartGridFeatures)
          ? heatPumpSmartGridFeatures?.properties?.actualState?.value
          : null;
      }
    );
  }

  export function getBatteryNameBySerial(serialNumber: string): string {
    const serialFirst7Digits = serialNumber?.substring(0, 7);
    const nameBySerial: { [index: string]: string } = {
      7727520: '2.0A',
      7377123: '2.5A',
      7377124: '2.5B',
      7973596: '2.5A2',
    };

    return nameBySerial[serialFirst7Digits];
  }

  export function getCascadeDeviceById(
    deviceId: string
  ): MemoizedSelector<AppState, RichDevice, DefaultProjectorFn<RichDevice>> {
    return createSelector(getDevicesOfV2Cascade(), (cascadeDevices: RichDevice[]) =>
      cascadeDevices.find((device) => device.id === deviceId)
    );
  }

  export function getIsStoreAvailableForSrc(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(AuthSelectors.getVkorg(), (vkorg: string | undefined): boolean => {
      return vkorg && srcDeviceVkorgWhitelist.some((srcVkorg) => vkorg.includes(srcVkorg));
    });
  }

  export function selectAllSRCDevices(): MemoizedSelector<
    AppState,
    RichDevice[],
    DefaultProjectorFn<RichDevice[]>
  > {
    return createSelector(selectAllDevices, (devices: RichDevice[]) =>
      devices.filter((device) => device.roles.includes(DeviceRoles.SmartRoomDevice))
    );
  }

  export function selectIsAnySRCDeviceAssignedToHeatingCircuitWithSRCActive(
    circuitId: number,
    roomControlId: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      InstallationFeaturesSelectors.installationFeatures,
      ConfigurationSelectors.selectFeaturesBaseUri,
      InstallationDetailsSelectors.getInstallationId(),
      InstallationDetailsSelectors.getSelectedGateway(),
      selectAllSRCDevices(),
      (
        installationFeaturesState: InstallationFeaturesState,
        featuresBaseUri: string,
        stateInstallationId: number,
        stateGatewaySerial: Gateway,
        srcDevices: RichDevice[] = []
      ) => {
        const devicesFoundOnHeatingCircuit = [];

        srcDevices.forEach((device) => {
          const deviceHeatingCircuitIdUri = InstallationFeaturesHelpers.constructInstallationFeatureUri(
            featuresBaseUri,
            FeatureNames.deviceHeatingCircuitId,
            stateGatewaySerial.serial,
            stateInstallationId,
            device.id
          );

          const deviceHeatingCircuitIdFeature = installationFeaturesState.entities[
            deviceHeatingCircuitIdUri
          ] as Feature<IsNumberValueProperty, never>;
          if (deviceHeatingCircuitIdFeature?.properties?.value?.value === circuitId) {
            devicesFoundOnHeatingCircuit.push(deviceHeatingCircuitIdFeature);
          }
        });

        const roomStatusFeatureUri = InstallationFeaturesHelpers.constructInstallationFeatureUri(
          featuresBaseUri,
          FeatureNames.roomStatus,
          stateGatewaySerial.serial,
          stateInstallationId,
          roomControlId
        );

        const roomStatusFeatureEntity = installationFeaturesState.entities[
          roomStatusFeatureUri
        ] as Feature<RoomsStatusProperties, never>;

        const isSrcActive = roomStatusFeatureEntity?.properties?.active?.value;

        return isSrcActive && !!devicesFoundOnHeatingCircuit?.length;
      }
    );
  }

  export const getAllWallboxDevicesWithMatchingBoilerSerial = createSelector(
    getMainDevices(),
    selectAllDevices,
    (mainDevices, allDevices): WallboxDevicesWithMatchingBoilerSerial[] => {
      const wallboxDevicesOfSelectedGateway: WallboxDevicesWithMatchingBoilerSerial[] = [];
      // 2022.08.22 - it will cause problems in the future
      // We don't support eager data fetch for wallbox devices in multi gw scenario
      // setDataForWallboxDevice$ is triggered per gateway

      allDevices.forEach((device) => {
        if (device.roles.includes(DeviceRoles.VCS)) {
          const boilerSerial = mainDevices.find(
            (mainDevice) => mainDevice.gatewaySerial === device.gatewaySerial
          ).boilerSerial;
          const eebusIndex = getWallboxDeviceIndex(device);
          wallboxDevicesOfSelectedGateway.push({
            ...device,
            boilerSerial,
            wallboxIndex: `${boilerSerial}-${eebusIndex}`,
          });
        }
      });

      return wallboxDevicesOfSelectedGateway;
    }
  );

  export const getWallboxDevicesOfSelectedDevice = createSelector(
    getSelectedDevice,
    selectAllDevices,
    (selectedDevice, allDevices): RichDevice[] => {
      const wallboxDevicesOfSelectedGateway: RichDevice[] = allDevices.filter(
        (device) =>
          device.gatewaySerial === selectedDevice?.gatewaySerial &&
          device.roles.includes(DeviceRoles.VCS)
      );

      return wallboxDevicesOfSelectedGateway;
    }
  );

  export const isSelectedDeviceWallbox = createSelector(getSelectedDevice, (device): boolean =>
    device ? isWallboxDevice(device) : false
  );

  export function getCascadeDevicesNameForSpecificGateway(
    gatewaySerial: string
  ): MemoizedSelector<AppState, string[], DefaultProjectorFn<string[]>> {
    return createSelector(
      getDevicesOfV2Cascade(null, gatewaySerial),
      (cascadeDevices): string[] => {
        const deviceNames = cascadeDevices.map(
          (device) =>
            device.shortDeviceName ||
            '+INSTALLATIONS.INSTALLATION.CASCADES.MULTIVALENT.DEVICE.' +
              device.deviceType.toUpperCase()
        );

        return deviceNames;
      }
    );
  }

  export const isCommonDevice = createSelector(
    isEcoWaterDevice,
    isSelectedDeviceWallbox,
    (...selectors): boolean => selectors.every((isTypeActive) => !isTypeActive)
  );

  export const isLowBudgetDevice = createSelector(
    getSelectedDevice,
    (device: RichDevice): boolean =>
      [DeviceRoles.Vitodens100, DeviceRoles.Vitodens050].some((name) =>
        device?.roles?.includes(name)
      )
  );

  export const getSolarLogInverter = createSelector(
    selectAllDevices,
    (allDevices: RichDevice[]): RichDevice => {
      return allDevices.find(isSolarLogInverter);
    }
  );

  export const getSolarLogGateway = createSelector(
    selectAllDevices,
    (allDevices: RichDevice[]): RichDevice => allDevices.find(isSolarLogGateway)
  );

  export const mainDevicesForHeaderTiles = createSelector(
    getMainDevices(),
    getSolarLogInverter,
    (mainDevices, solarLogInverter): RichDevice[] => {
      return sortDevicesInTilesOrder<RichDevice>([...mainDevices, solarLogInverter]);
    }
  );

  export const selectIsSelectedDeviceVentilationDevice: MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > = createSelector(
    DevicesSelectors.getCurrentlySelectedDevice(),
    (selectedDevice: RichDevice) => selectedDevice?.deviceType === DeviceTypes.Ventilation
  );

  export function isSelectedDeviceWithError(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      getSelectedDevice,
      InstallationFeaturesSelectors.selectFeaturesError,
      (device, error): boolean => !!device.error || !!error
    );
  }

  export const getPhotovoltaicDevice = createSelector(
    selectAllDevices,
    (allDevices: RichDevice[]): RichDevice => allDevices.find(isPhotovoltaic)
  );

  export function deviceTimeOffset(): MemoizedSelector<
    AppState,
    number,
    DefaultProjectorFn<number>
  > {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationDeviceFeature(
        FeatureNames.heatingDeviceTimeOffset
      ),
      (heatingDeviceTimeOffsetFeature: Feature<IsNumberValueProperty, never>): number => {
        const offsetTimeFeature = heatingDeviceTimeOffsetFeature?.feature[
          FeatureNames.heatingDeviceTimeOffset
        ] as Feature<IsNumberValueProperty, never>;
        if (
          offsetTimeFeature?.properties?.value?.value &&
          offsetTimeFeature?.properties?.value?.unit === Unit.Minute
        ) {
          return offsetTimeFeature.properties.value.value;
        }

        return null;
      }
    );
  }

  export function getDeviceId(
    deviceSerial: string
  ): MemoizedSelector<AppState, string, DefaultProjectorFn<string>> {
    return createSelector(selectAllDevices, (devicesWithFeatures: RichDevice[]) => {
      const dev: RichDevice = devicesWithFeatures.find((d) => d.boilerSerial === deviceSerial);

      return dev?.id;
    });
  }

  export function isHeatingDeviceTimeAvailable(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationDeviceFeature(FeatureNames.heatingDeviceTime),
      (heatingDeviceTimeFeature: Feature<never, HeatingDeviceTimeCommands>) => {
        return (
          isReady(heatingDeviceTimeFeature) &&
          heatingDeviceTimeFeature.commands?.setTime?.isExecutable
        );
      }
    );
  }

  export function isLegacyDeviceWithoutBoilerSerial(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(DevicesSelectors.getCurrentlySelectedDevice(), (device: RichDevice) => {
      if (device) {
        return !device.boilerSerial && device.roles.includes(DeviceRoles.Legacy);
      } else {
        return false;
      }
    });
  }

  export function selectDeviceBusinessName(): MemoizedSelector<
    AppState,
    string,
    DefaultProjectorFn<string>
  > {
    return createSelector(
      DevicesSelectors.isE3Device(),
      getSelectedDevice,
      InstallationDetailsSelectors.getSelectedGateway(),
      (isE3Device, selectedDevice, selectedGateway) => {
        if (isE3Device) {
          return selectedDevice.shortDeviceName;
        } else {
          return selectedGateway.gatewayType;
        }
      }
    );
  }

  export function getCascadeMemberId(
    deviceId: string
  ): MemoizedSelector<AppState, number, DefaultProjectorFn<number>> {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationDeviceFeature<
        TypedFeatureProperties<number>,
        never
      >(FeatureNames.cascadeMemberId, deviceId),
      (memberId) => memberId?.properties?.value?.value
    );
  }

  export function selectAllSolarLogPvImmersionHeater(): MemoizedSelector<
    AppState,
    RichDevice[],
    DefaultProjectorFn<RichDevice[]>
  > {
    return createSelector(selectAllDevices, (allDevices) => {
      const solarLogPVHeaterDevices: RichDevice[] = allDevices.filter((device) =>
        device.roles.includes(DeviceRoles.SolarLogPVHeater)
      );

      return solarLogPVHeaterDevices;
    });
  }

  export function isInternalHydraulicAvailable(
    deviceId: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationDeviceFeature(
        FeatureNames.volumetricFlowReturn,
        deviceId
      ),
      InstallationFeaturesSelectors.selectInstallationDeviceFeature(
        FeatureNames.secondaryValvesFourThreeWay,
        deviceId
      ),
      InstallationFeaturesSelectors.selectInstallationDeviceFeature(
        FeatureNames.boilerCommonSupplyTemperature,
        deviceId
      ),
      InstallationFeaturesSelectors.selectInstallationDeviceFeature(
        FeatureNames.returnFlowTemperature,
        deviceId
      ),
      (
        volumetricFlowReturnFeature: Feature<HeatingBoilerPumpInternalProperties, never>,
        secondaryValvesFourThreeWayFeature: Feature<DcSensorValueProperties, never>,
        boilerCommonSupplyTemperatureFeature: Feature<
          HeatingSecondaryCircuitValvesFourThreeWayProperties,
          never
        >,
        returnFlowTemperatureFeature: Feature<DcSensorValueProperties, never>
      ) => {
        return (
          isReady(volumetricFlowReturnFeature) ||
          isReady(secondaryValvesFourThreeWayFeature) ||
          isReady(boilerCommonSupplyTemperatureFeature) ||
          isReady(returnFlowTemperatureFeature)
        );
      }
    );
  }

  export function selectAnomalyListForDevice(
    boilerSerial: string
  ): MemoizedSelector<AppState, DiagnosisData[], DefaultProjectorFn<DiagnosisData[]>> {
    return createSelector(devicesState, (state: DevicesState): DiagnosisData[] | null => {
      const deviceWithAnomaly = state.diagnosticDataArray?.find(
        (diagnosticsData) =>
          diagnosticsData.data?.boiler_id === boilerSerial &&
          diagnosticsData.data?.data.some((item) => item.diagnosis_anomaly === DIAGNOSIS_ANOMALY)
      );
      if (!deviceWithAnomaly) {
        return null;
      }
      const anomalies = deviceWithAnomaly.data.data.filter(
        (item) => item.diagnosis_anomaly === DIAGNOSIS_ANOMALY
      );

      return anomalies;
    });
  }

  export function isHemsActivated(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(DevicesSelectors.selectDeviceEntities, (deviceEntities) =>
      Object.values(deviceEntities).some(
        (device) => device.id === DeviceIds.Hems && device.roles.includes(DeviceRoles.VirtualHems)
      )
    );
  }

  export function selectDeviceByRole(
    role: DeviceRoles
  ): MemoizedSelector<AppState, RichDevice[], DefaultProjectorFn<RichDevice[]>> {
    return createSelector(selectAllDevices, (devices): RichDevice[] =>
      devices.filter((device) => device.roles.includes(role))
    );
  }

  export function selectShouldUseConsolidatedFeature(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      selectDeviceByRole(DeviceRoles.ConsolidatorElectric),
      (devices): boolean => devices.length > 0
    );
  }

  export function selectFeatureForEnergyStorageDataTrending(): MemoizedSelector<
    AppState,
    string[],
    DefaultProjectorFn<string[]>
  > {
    return createSelector(
      selectDeviceByRole(DeviceRoles.ProductVitocharge),
      selectBatteryWithMostValues(),
      (vitoChargeDevices: RichDevice[], batteryWithMostValues: TrendingCategory) => {
        const isIndividualVitocharge = vitoChargeDevices && vitoChargeDevices.length === 1;
        const energyStorage = cloneDeep(energyStorageProperties).filter((property) => {
          return isIndividualVitocharge
            ? !energyStorageTotalPropertiesForMultipleVitocharge.includes(property)
            : !energyStorageTotalPropertiesForIndividualVitocharge.includes(property);
        });
        const dlEssInverterAcPowerConsolidated =
          vitoChargeDevices.map((_item, index) =>
            FeatureNames.dlEssInverterAcPowerConsolidatedItem(index + 1)
          ) || [];
        const dlEssPowerConsolidated =
          vitoChargeDevices.map((_item, index) =>
            FeatureNames.dlEssPowerConsolidatedItem(index + 1)
          ) || [];
        const dlEssStateOfChargeConsolidated =
          vitoChargeDevices.map((_item, index) =>
            FeatureNames.dlEssStateOfChargeConsolidated(index + 1)
          ) || [];
        const dlEssBatteryNinformationStateOfCharge = batteryWithMostValues
          ? batteryWithMostValues.items.map((_item, index) =>
              FeatureNames.dlEssBatteryInformationStateOfCharge(index)
            )
          : [];
        const dlEssBatteryNoperatingDataVoltage = batteryWithMostValues
          ? batteryWithMostValues.items.map((_item, index) =>
              FeatureNames.dlEssBatteryOperatingDataVoltage(index)
            )
          : [];
        const dlEssBatteryNoperatingDataTemperatureSensorsInternal = batteryWithMostValues
          ? batteryWithMostValues.items.flatMap((_battery, batteryIndex) =>
              Array.from({ length: 8 }).map((_, sensorIndex) =>
                FeatureNames.essBatteryOperatingDataTemperatureSensorsInternal(
                  batteryIndex,
                  sensorIndex + 1
                )
              )
            )
          : [];

        return [
          ...energyStorage,
          ...dlEssInverterAcPowerConsolidated,
          ...dlEssPowerConsolidated,
          ...dlEssStateOfChargeConsolidated,
          ...dlEssBatteryNinformationStateOfCharge,
          ...dlEssBatteryNoperatingDataVoltage,
          ...dlEssBatteryNoperatingDataTemperatureSensorsInternal,
        ];
      }
    );
  }

  export function selectBatteryWithMostValues(): MemoizedSelector<
    AppState,
    TrendingCategory,
    DefaultProjectorFn<TrendingCategory>
  > {
    return createSelector(devicesState, (state: DevicesState) => {
      if (!state.batteryModuleCategories || state.batteryModuleCategories.length === 0) {
        return null;
      }

      const batteryWithMostValues: TrendingCategory = state.batteryModuleCategories.reduce(
        (maxItem, currentItem) => {
          return currentItem.items.length > maxItem.items.length ? currentItem : maxItem;
        }
      );

      return batteryWithMostValues;
    });
  }

  export function selectBatteryModuleCategory(): MemoizedSelector<
    AppState,
    TrendingCategory[],
    DefaultProjectorFn<TrendingCategory[]>
  > {
    return createSelector(
      devicesState,
      TrendingSelectors.getDataSeries(),
      (state: DevicesState, dataSeries: TrendingData) => {
        if (!state.batteryModuleCategories || state.batteryModuleCategories.length === 0) {
          return [];
        }

        return state.batteryModuleCategories.map((batteryModuleCategory) => {
          return {
            ...batteryModuleCategory,
            items: batteryModuleCategory.items.map((item) => {
              return {
                ...item,
                features: dataSeries.series
                  .filter(
                    (series) =>
                      series.deviceId === batteryModuleCategory.deviceId &&
                      (series.feature.includes(
                        `dl.ess.battery.${item.categoryId}.operating.data.temperature.sensors.internal`
                      ) ||
                        series.feature === FeatureNames.dlEssSensorsTemperatureAmbient)
                  )
                  .map((series) => series.feature),
                isShow: dataSeries.series.some((series) =>
                  series.feature.includes(
                    `dl.ess.battery.${item.categoryId}.operating.data.temperature.sensors.internal`
                  )
                ),
              };
            }),
          };
        });
      }
    );
  }
}
